import { useCallback } from 'react';
import { assert } from '@chainflip/utils/assertion';
import { type ChainflipChain } from '@chainflip/utils/chainflip';
import { useMutation } from '@tanstack/react-query';
import { validateChainAddress } from '@/shared/assets/chains/addressValidation';
import { chainflipChainMap } from '@/shared/utils/env';
import { useExtrinsicSDK, useStateChainAccount } from './useStateChainAccount';
import useStore from './useStore';

/**
 * with evm deposit channel onboarding, we need to save refund addresses
 * temporarily in a zustand store so we can submit that address to the broker
 * when opening a deposit channel
 *
 * for regular state chain accounts or already onboarded evm accounts, we submit
 * the extrinsic to update the refund address on chain
 */
export default function useUpdateRefundAddress({
  chain: chainflipChain,
  setAddressInputError,
  address,
  onComplete,
}: {
  chain: ChainflipChain | undefined;
  setAddressInputError: (error: string | undefined) => void;
  address: string;
  onComplete?: () => void;
}) {
  const { account } = useStateChainAccount();
  const { sendExtrinsic, toast } = useExtrinsicSDK('setRefundAddress');
  const setRefundAddressInStore = useStore((state) => state.setRefundAddress);

  const chain = chainflipChain && chainflipChainMap[chainflipChain];
  const isValidAddress = useCallback(
    () => (chain ? validateChainAddress(address)[chain.id] : false),
    [address, chain?.id],
  );

  return useMutation({
    mutationFn: async (_: React.MouseEvent<HTMLButtonElement>) => {
      let hadError = false;

      assert(chain, 'Chainflip chain is required to set refund address');

      if (!address) {
        setAddressInputError('A refund address is required');
        hadError = true;
      } else if (!isValidAddress()) {
        setAddressInputError(`A valid ${chain.name} address is required`);
        hadError = true;
      }

      if (hadError) return Promise.reject();

      if (account) {
        toast.loading(`Saving ${chain.name} refund address...`);

        await sendExtrinsic(chain.chainflipId, address);
      } else {
        // if they are onboarding via deposit channel
        setRefundAddressInStore(chain.chainflipId, address);
      }
    },
    onSuccess() {
      onComplete?.();
      toast.success(
        account
          ? `You've successfully set your ${chain?.name} refund address.`
          : `You’ve successfully set your ${chain?.name} refund address. Make sure to click “Deposit” to make your first deposit and complete your Chainflip account setup. Otherwise, if you disconnect your wallet, you’ll need to set up your refund addresses again.`,
      );
    },
    onError(err) {
      toast.error('Error while submitting transaction', {
        description: err.message,
      });
    },
  });
}
